import { getContractTypeName } from '@/app/common/converters/contractTypeToTextConverter'
import { getSettlementTypeName } from '@/app/common/converters/settlementTypeToTextConverter'
import { getWorkTimeName } from '@/app/common/converters/workTimeToTextConverter'
import { getContractType } from '@/app/services/contract/contractTypeResolver'
import { getSettlementType } from '@/app/services/contract/settlementTypeResolver'
import {
  AmountsPreviewPeriod,
  useAmountsPreviewQuery,
} from '@/lib/api/company/timesheet/amountsPreview'
import {
  ForeignAmountsCountryPeriod,
  useForeignAmountsPreviewQuery,
} from '@/lib/api/company/timesheet/foreignAmounts'
import { faClose, FaIcon } from '@/lib/fa'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import {
  AmountsTable,
  drawExtendedRow,
  drawHeaderRow,
  drawSummaryRow,
  formatNumber,
} from '@/modules/drivers/shared/timesheet-amounts'
import { ForeignAmountSingleCountry } from '@/modules/drivers/shared/timesheet-amounts/ForeignAmountSingleCountry'
import {
  SettingsSectionTab,
  SettingsSectionWithTabs,
} from '@/modules/settings/components/SettingsSectionWithTabs'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { formatDate } from '@/modules/shared/utils/date'
import { Loader } from '@/ui'
import React from 'react'

type AmountsTab = 'pln' | 'foreign'

type AmountsModalProps = {
  companyId: string
  driverId: string
  yearMonth: string
  onClose: () => void
  initialTab?: AmountsTab
}

const AmountsModal = ({
  companyId,
  driverId,
  yearMonth,
  onClose,
  initialTab = 'pln',
}: AmountsModalProps) => {
  const { t } = useLocalize()
  const [activeTab, setActiveTab] = React.useState<AmountsTab>(initialTab)

  const amountsQuery = useAmountsPreviewQuery({
    companyId,
    driverId,
    yearMonth,
  })
  const foreignAmountsQuery = useForeignAmountsPreviewQuery({
    companyId,
    driverId,
    yearMonth,
  })

  const rows = (amountsQuery.data || []) as AmountsPreviewPeriod[]
  const foreignRows = (foreignAmountsQuery.data || []) as
    ForeignAmountsCountryPeriod[]
  const data = rows.find((period) => period.periodStartDate.includes(yearMonth))
  const isForeignTab = activeTab === 'foreign'
  const activeQuery = isForeignTab ? foreignAmountsQuery : amountsQuery
  const hasActiveData = isForeignTab ? foreignRows.length > 0 : Boolean(data)

  return (
    <ModalLayout customZIndex={9999}>
      <div className="tw-relative tw-flex tw-max-h-[80vh] tw-w-full tw-max-w-[80vw] tw-flex-col tw-gap-4 tw-rounded-lg tw-bg-white tw-p-4">
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
          <h1 className="tw-mt-1 tw-text-xl tw-text-base-content">
            {t('menu.amounts-preview')}
          </h1>
          <FaIcon
            icon={faClose}
            onClick={onClose}
            className="tw-cursor-pointer"
          />
        </div>

        <div className="tw-flex tw-gap-4 tw-border-b tw-border-[#e5e6e6]">
          <button
            type="button"
            aria-selected={activeTab === 'pln'}
            className={`tw-border-b-2 tw-px-2 tw-pb-2 tw-text-sm tw-font-medium ${
              activeTab === 'pln'
                ? 'tw-border-[#2448bb] tw-text-[#2448bb]'
                : 'tw-border-transparent tw-text-[#6b77a0]'
            }`}
            onClick={() => setActiveTab('pln')}
          >
            PLN
          </button>
          <button
            type="button"
            aria-selected={activeTab === 'foreign'}
            className={`tw-border-b-2 tw-px-2 tw-pb-2 tw-text-sm tw-font-medium ${
              activeTab === 'foreign'
                ? 'tw-border-[#2448bb] tw-text-[#2448bb]'
                : 'tw-border-transparent tw-text-[#6b77a0]'
            }`}
            onClick={() => setActiveTab('foreign')}
          >
            EUR / {t('menu.foreign-amounts')}
          </button>
        </div>

        {activeQuery.isFetching && (
          <Loader position="absolute" className="tw-h-full tw-w-full" />
        )}

        {!activeQuery.isFetching && activeQuery.isSuccess && !hasActiveData ? (
          <div>
            <p>{t('common.no-data')}</p>
          </div>
        ) : null}

        <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
          {!isForeignTab && data && (
            <DriverAmountsPreviewContent period={data} />
          )}

          {isForeignTab && foreignRows.length > 0 && (
            <SettingsSectionWithTabs
              fullWidth
              className="tw-mb-0 tw-p-0 tw-shadow-none"
              classNameNavigation="tw-w-[160px] tw-flex-none"
            >
              {foreignRows.map((countryPeriod) => {
                const countryTabName =
                  countryPeriod.country.code || countryPeriod.country.name

                return (
                  <SettingsSectionTab
                    key={`${countryTabName}-${countryPeriod.periodStartDate}`}
                    name={countryTabName}
                    title={countryPeriod.country.name}
                  >
                    <ForeignAmountSingleCountry data={countryPeriod} />
                  </SettingsSectionTab>
                )
              })}
            </SettingsSectionWithTabs>
          )}
        </div>
      </div>
    </ModalLayout>
  )
}

export default AmountsModal

const DriverAmountsPreviewContent = ({
  period,
}: {
  period: AmountsPreviewPeriod
}) => {
  const { t } = useLocalize()

  const startDate = formatDate(period.periodStartDate, 'YYYY-MM-DD')
  const endDate = formatDate(period.periodEndDate, 'YYYY-MM-DD')

  const amounts = period
  const translate = t

  const sumOfAdditionalParts = amounts.additionalParts.reduce((prev, cur) => {
    return prev + cur.amount
  }, 0)

  const sumOfAdditionalAmountsNotIncludedInMinimalPayment =
    amounts.additionalAmountsNotIncludedInMinimalPayment.reduce((prev, cur) => {
      return prev + cur.amount
    }, 0)

  const sumOfAdditionalAmountsForSettlementPeriod =
    amounts.additionalAmountsForSettlementPeriod.reduce((prev, cur) => {
      return prev + cur.amount
    }, 0)

  const sumOfDeductions = amounts.deductions.reduce((prev, cur) => {
    return prev + cur.amount
  }, 0)

  const sumOfAmountBasisWithoutDeductions =
    amounts.amountBasis.amount - sumOfDeductions

  return (
    <div className="tw-relative tw-flex tw-flex-col tw-gap-6">
      <AmountsTable>
        <tbody>
          <tr>
            <th>{t('amountsPreview.period-title')}</th>
            <td>{`${startDate} - ${endDate}`}</td>
          </tr>
          <tr>
            <th>{t('amountsPreview.contract-type')}</th>
            <td>{getContractTypeName(getContractType(amounts.type), translate)}</td>
          </tr>
          <tr>
            <th>{translate('amountsPreview.contract-worktime')}</th>
            <td>{getWorkTimeName(amounts.workTime, translate)}</td>
          </tr>
          <tr>
            <th>{translate('amountsPreview.contract-settlement')}</th>
            <td>{getSettlementTypeName(getSettlementType(amounts.settlement), translate)}</td>
          </tr>
        </tbody>
      </AmountsTable>

      <AmountsTable>
        <tbody>
          <tr>
            <td>{translate('amountsPreview.plannedHoursRate')}</td>
            <th>{amounts.periodRate}</th>
          </tr>
          <tr>
            <td>{translate('amountsPreview.plannedHoursRate')}</td>
            <th>{amounts.periodHourRate}</th>
          </tr>
          <tr>
            <td>{translate('amountsPreview.monthWorkingDaysHourRate')}</td>
            <th>{amounts.monthWorkingDaysHourRate}</th>
          </tr>
          <tr>
            <th colSpan={2}>
              {translate('amountsPreview.ratesForMinimalPayment')}
            </th>
          </tr>
          {/* {drawRow(translate("amountsPreview.hourRate"), amounts.plannedHoursMinimalHourRate)} */}

          <tr>
            <td>{translate('amountsPreview.monthRate')}</td>
            <th>{amounts.minimalPeriodRate}</th>
          </tr>
          <tr>
            <td>{translate('amountsPreview.workingHoursMinimalHourRate')}</td>
            <th>{amounts.workingHoursMinimalHourRate}</th>
          </tr>
          <tr>
            <td>{translate('amountsPreview.minimalAmountForWorkingHours')}</td>
            <th>{amounts.minimalAmountForWorkingHours}</th>
          </tr>
        </tbody>
      </AmountsTable>

      <AmountsTable>
        <thead>
          <tr className="tw-capitalize">
            <th>{translate('amountsPreview.partName')}</th>
            <th>{translate('amountsPreview.partDays')}</th>
            <th>{translate('amountsPreview.partHours')}</th>
            <th>{translate('amountsPreview.partRate')}</th>
            <th>{translate('amountsPreview.partAmount')}</th>
          </tr>
        </thead>
        <tbody>
          {drawHeaderRow(translate('amountsPreview.baseSalary'))}
          {drawExtendedRow(
            '1',
            amounts.amountBasis.eventName,
            '',
            amounts.amountBasis.hours,
            amounts.amountBasis.rates,
            formatNumber(amounts.amountBasis.amount),
          )}
          {amounts.deductions.map((d, i) => {
            return drawExtendedRow(
              i.toString(),
              d.eventName,
              d.days,
              d.hours,
              d.rates,
              formatNumber(d.amount),
            )
          })}
          {drawSummaryRow(formatNumber(sumOfAmountBasisWithoutDeductions))}

          {drawHeaderRow(translate('amountsPreview.additionalParts'))}
          {amounts.additionalParts.map((d, i) => {
            return drawExtendedRow(
              i.toString(),
              d.eventName,
              '',
              d.hours,
              d.rates,
              formatNumber(d.amount),
            )
          })}
          {drawSummaryRow(formatNumber(sumOfAdditionalParts))}

          {drawHeaderRow(
            translate(
              'amountsPreview.additionalAmountsNotIncludedInMinimalPayment',
            ),
          )}
          {amounts.additionalAmountsNotIncludedInMinimalPayment.map((d, i) => {
            return drawExtendedRow(
              i.toString(),
              d.eventName,
              '',
              d.hours,
              d.rates,
              formatNumber(d.amount),
            )
          })}
          {drawSummaryRow(
            formatNumber(sumOfAdditionalAmountsNotIncludedInMinimalPayment),
          )}

          {sumOfAdditionalAmountsForSettlementPeriod > 0 && (
            <>
              {drawHeaderRow(
                translate('amountsPreview.additionalPartsFromSettlementPeriod'),
              )}
              {amounts.additionalAmountsForSettlementPeriod.map((d, i) => {
                return drawExtendedRow(
                  i.toString(),
                  d.eventName,
                  '',
                  d.hours,
                  d.rates,
                  formatNumber(d.amount),
                )
              })}
              {drawSummaryRow(
                formatNumber(sumOfAdditionalAmountsForSettlementPeriod),
              )}
            </>
          )}

          {drawSummaryRow(
            formatNumber(
              sumOfAmountBasisWithoutDeductions +
                sumOfAdditionalParts +
                sumOfAdditionalAmountsNotIncludedInMinimalPayment +
                sumOfAdditionalAmountsForSettlementPeriod,
            ),
            translate('amountsPreview.summary'),
            true,
          )}

          {(amounts.contractDeductions || []).length > 0 && (
            <>
              {drawHeaderRow(translate('amountsPreview.contractDeductions'))}
              {(amounts.contractDeductions || []).map((d, i) => {
                return drawExtendedRow(
                  i.toString(),
                  d.eventName,
                  '',
                  d.hours,
                  d.rates,
                  formatNumber(d.amount),
                )
              })}
              {drawSummaryRow(
                formatNumber(
                  (amounts.contractDeductions || []).reduce(
                    (prev, cur) => prev + cur.amount,
                    0,
                  ),
                ),
              )}
            </>
          )}

          {amounts.deductionsForAbsenceDays != null &&
            amounts.deductionsForAbsenceDays.length > 0 && (
              <>
                {drawHeaderRow(translate('amountsPreview.amountsDeductions'))}
                {amounts.deductionsForAbsenceDays.map((d, i) => {
                  return drawExtendedRow(
                    i.toString(),
                    d.eventName,
                    d.days,
                    d.hours,
                    '',
                    '---------------------',
                  )
                })}
              </>
            )}
        </tbody>
      </AmountsTable>
    </div>
  )
}
